import React, { useEffect, useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { withScoutTheme } from '../ThemeContext/ThemeContext';
import Api from './API';
import useStyles from './useStyles';
import ScoutSelect from '../scout/select';

function StatusCategories({ entityId, apiKey }) {
  const classes = useStyles();
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [differentFromDefaults, setDifferentFromDefaults] = useState(false);
  const [hasParentSettings, setHasParentSettings] = useState(false);
  const [error, setError] = useState();

  const fetchCategories = async () => {
    try {
      setError(undefined);
      const data = await Api.getCategories(entityId);
      return data;
    } catch (err) {
      setError(err.message);
      return;
    }
  };

  const initialiseCategories = async () => {
    setLoading(true);
    const data = await fetchCategories();

    setCategories(data.categories);
    setStatuses(data.statuses);
    setDefaults(data.defaults);
    setHasParentSettings(data.has_parent_settings);
    setDifferentFromDefaults(defaultsDiff(data.categories, data.defaults));
    setLoading(false);
  };

  useEffect(() => {
    localStorage.setItem('userKey', apiKey);
    initialiseCategories();
  }, []);

  const updateCategories = async (cats) => {
    setDirty(false);
    setSaving(true);
    setSaved(false);

    const params = {
      category_assignment: { categories: cats }
    };

    try {
      setError(undefined);
      await Api.updateCategories(entityId, params);
      setSaved(true);
      setCategories(cats);
      setDifferentFromDefaults(defaultsDiff(cats, defaults));
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
    setTimeout(() => {
      setSaved(false);
    }, 3000);
  };

  const createDefaultCategories = async () => {
    try {
      const { categories } = await Api.createDefaultCategories(entityId);
      setCategories(categories);
      setDifferentFromDefaults(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeCategories = async () => {
    try {
      setError(undefined);
      const { categories } = await Api.removeCategories(entityId);
      setCategories([]);
      setDifferentFromDefaults(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const resetCategories = async () => {
    const newCategories = categories.map((category) => {
      return { ...category, status_id: defaults[category.name] };
    });
    await updateCategories(newCategories);
  };

  const saveCategories = () => updateCategories(categories);

  const handleStatusChanged = (category, id) => {
    if (toS(category.status_id) === toS(id)) {
      return;
    }

    category.status_id = id;
    setDirty(true);
  };

  const toS = (obj) => (obj || '') + '';

  const categoryColumn = (category) => {
    return (
      <div className="category-column" key={category.id}>
        <div className="category-column-header">{category.name}</div>
        <ScoutSelect
          name="category_status"
          value={toS(category.status_id)}
          data={statusOptions}
          onChange={(id) => handleStatusChanged(category, id)}
        />
      </div>
    );
  };

  const defaultCategoryColumn = (categoryName, statusId) => {
    const status = statuses.find(({ id }) => id == statusId);
    return (
      <div className="category-column" key={categoryName}>
        <div className="category-column-header">{categoryName}</div>
        <div>{status ? status.name : 'New'}</div>
      </div>
    );
  };

  const defaultsDiff = (collection, defaultsData) =>
    collection.some((category) => toS(category.status_id) !== toS(defaultsData[category.name]));

  const statusOptions = statuses.map((status) => ({ label: status.name, value: toS(status.id) }));

  return (
    <div className={classes.root}>
      <div
        className="modal fade"
        id="statusCategoriesModal"
        tabIndex="-1"
        aria-labelledby="statusCategoriesModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="mt-0">Status Categories for Hiring Manager App</h2>
              <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body">
              {loading ? (
                <div className="d-flex justify-content-center">
                  <CircularProgress />
                </div>
              ) : (
                <div className="categories-board">
                  {categories.length > 0 && categories.map((category) => categoryColumn(category))}
                  {categories.length == 0 &&
                    Object.keys(defaults).map((categoryName) =>
                      defaultCategoryColumn(categoryName, defaults[categoryName])
                    )}
                </div>
              )}
            </div>
            {!loading && (
              <div className={`modal-footer ${classes.footer}`}>
                {error && <div className="alert alert-danger">{error}</div>}
                {saved && <div className="alert alert-success">Saved</div>}
                {categories.length == 0 && (
                  <Button variant="text" onClick={createDefaultCategories}>
                    Use custom setting
                  </Button>
                )}
                {hasParentSettings && categories.length > 0 && (
                  <Button variant="text" onClick={removeCategories}>
                    Use parent setting
                  </Button>
                )}
                {categories.length > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!dirty}
                    onClick={saveCategories}
                    startIcon={saving ? <CircularProgress size={12} color="inherit" /> : null}
                  >
                    Save
                  </Button>
                )}
                {differentFromDefaults && (
                  <Button variant="outlined" color="secondary" onClick={resetCategories}>
                    Reset to default
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default withScoutTheme(StatusCategories);
